import { Chart, Plugin } from 'chart.js';

type Bag = Record<string, unknown>;

interface ChartThemeValues {
	tick: string | null;
	grid: string | null;
	label: string | null;
	surface: string | null;
	font: string | null;
}

interface ThemedValue {
	original: unknown;
	applied: string;
}

// TRAP: Chart.js rebuilds a chart's scale options on every update, copying
// them from the ones changed here, so a colour set once stays set and the
// objects that held the originals are gone. The original is kept per chart and
// per option path instead, and put back only while the value is still ours —
// ng2-charts hands a chart fresh options whenever its inputs change.
const THEMED = new WeakMap<Chart, Map<string, ThemedValue>>();

const WHITE = new Set(['#fff', '#ffffff', 'white']);

function readTheme(canvas: HTMLCanvasElement): ChartThemeValues {
	const style = getComputedStyle(canvas);
	const read = (name: string): string | null =>
		style.getPropertyValue(name).trim() || null;
	return {
		tick: read('--report-chart-tick'),
		grid: read('--report-chart-grid'),
		label: read('--report-chart-label'),
		surface: read('--report-chart-surface'),
		font: read('--report-chart-font')
	};
}

function child(target: Bag, key: string, create: boolean): Bag | undefined {
	const existing = target[key];
	if (existing && typeof existing === 'object') {
		return existing as Bag;
	}
	if (!create) return undefined;
	const made: Bag = {};
	target[key] = made;
	return made;
}

class ChartThemer {
	private readonly entries: Map<string, ThemedValue>;

	constructor(chart: Chart, private readonly theme: ChartThemeValues) {
		let entries = THEMED.get(chart);
		if (!entries) {
			entries = new Map<string, ThemedValue>();
			THEMED.set(chart, entries);
		}
		this.entries = entries;
	}

	get creates(): boolean {
		return Boolean(this.theme.tick || this.theme.font);
	}

	isOurs(path: string, target: Bag, key: string): boolean {
		const entry = this.entries.get(path);
		return Boolean(entry) && target[key] === entry!.applied;
	}

	set(
		path: string,
		target: Bag | undefined,
		key: string,
		value: string | null
	): void {
		if (!target) return;
		const entry = this.entries.get(path);
		const current = target[key];
		const ours = Boolean(entry) && current === entry!.applied;
		if (value) {
			this.entries.set(path, {
				original: ours ? entry!.original : current,
				applied: value
			});
			target[key] = value;
			return;
		}
		if (!entry) return;
		if (ours) {
			if (entry.original === undefined) {
				delete target[key];
			} else {
				target[key] = entry.original;
			}
		}
		this.entries.delete(path);
	}

	scale(id: string, scale: Bag): void {
		const { tick, grid, label, font } = this.theme;
		const at = `scales.${id}`;
		const ticks = child(scale, 'ticks', this.creates);
		this.set(`${at}.ticks.color`, ticks, 'color', tick);
		this.set(
			`${at}.ticks.font.family`,
			ticks && child(ticks, 'font', this.creates),
			'family',
			font
		);
		this.set(
			`${at}.grid.color`,
			child(scale, 'grid', this.creates),
			'color',
			grid
		);
		this.set(
			`${at}.angleLines.color`,
			child(scale, 'angleLines', false),
			'color',
			grid
		);
		this.set(
			`${at}.title.color`,
			child(scale, 'title', false),
			'color',
			tick
		);
		const pointLabels = child(scale, 'pointLabels', false);
		this.set(`${at}.pointLabels.color`, pointLabels, 'color', label);
		this.set(
			`${at}.pointLabels.font.family`,
			pointLabels && child(pointLabels, 'font', this.creates),
			'family',
			font
		);
	}

	plugins(plugins: Bag): void {
		const { grid, label, font } = this.theme;
		const legend = child(plugins, 'legend', false);
		const labels = legend && child(legend, 'labels', this.creates);
		this.set('plugins.legend.labels.color', labels, 'color', label);
		this.set(
			'plugins.legend.labels.font.family',
			labels && child(labels, 'font', this.creates),
			'family',
			font
		);

		const values = child(plugins, 'barValueLabels', false);
		this.set('plugins.barValueLabels.color', values, 'color', label);
		this.set(
			'plugins.barValueLabels.fontFamily',
			values,
			'fontFamily',
			font
		);

		const polar = child(plugins, 'polarAreaLabels', false);
		this.set('plugins.polarAreaLabels.color', polar, 'color', label);
		this.set(
			'plugins.polarAreaLabels.spokeColor',
			polar,
			'spokeColor',
			grid
		);
		this.set(
			'plugins.polarAreaLabels.fontFamily',
			polar,
			'fontFamily',
			font
		);

		this.set(
			'plugins.segmentLabels.fontFamily',
			child(plugins, 'segmentLabels', false),
			'fontFamily',
			font
		);
	}

	datasets(datasets: Bag[]): void {
		const { surface, font } = this.theme;
		datasets.forEach((dataset, index) => {
			const at = `datasets.${index}`;
			const border = dataset['borderColor'];
			if (
				(typeof border === 'string' &&
					WHITE.has(border.trim().toLowerCase())) ||
				this.isOurs(`${at}.borderColor`, dataset, 'borderColor')
			) {
				this.set(`${at}.borderColor`, dataset, 'borderColor', surface);
			}
			if (typeof dataset['family'] === 'string') {
				this.set(`${at}.family`, dataset, 'family', font);
			}
		});
	}
}

export function applyReportChartTheme(chart: Chart): void {
	const canvas = chart.canvas;
	const options = chart.config?.options as unknown as Bag | undefined;
	if (!canvas || !options) return;
	const theme = readTheme(canvas);
	if (!Object.values(theme).some(Boolean) && !THEMED.has(chart)) return;
	const themer = new ChartThemer(chart, theme);

	const scales = child(options, 'scales', false);
	for (const [id, scale] of Object.entries(scales ?? {})) {
		if (scale && typeof scale === 'object') {
			themer.scale(id, scale as Bag);
		}
	}
	const plugins = child(options, 'plugins', false);
	if (plugins) {
		themer.plugins(plugins);
	}
	themer.datasets((chart.config.data?.datasets ?? []) as unknown as Bag[]);
}

// CONTRACT: every report surface sets the --report-chart-* properties on its
// root (ReportThemeService), and the canvas inherits them. A chart anywhere
// else finds none and is left exactly as it was built.
export const reportChartThemePlugin: Plugin = {
	id: 'reportChartTheme',
	beforeInit: chart => applyReportChartTheme(chart),
	beforeUpdate: chart => applyReportChartTheme(chart)
};

Chart.register(reportChartThemePlugin);
